import { TenantApiError, dispatchSessionExpired, MESSAGES } from './tenantApiErrors';
import { authTokenService } from './auth.service';

export interface EmergencyContact {
  id: string;
  name: string;
  phone: string;
  relationship: string;
}

export interface Sponsor {
  id: string;
  name: string;
  phone: string;
  relationship: string;
}

export interface TenantData {
  id: string;
  name: string;
  email: string;
  phone: string;
  firebaseUid: string | null;
  isAuthenticated: boolean;
  lastLoginAt: string | null;
  unitId: string | null;
  idType: string;
  idNumber: string;
  profilePhoto?: string;
  employmentStatus: string;
  employer?: string;
  position?: string;
  status: string;
  moveInDate: string;
  rentDueDay: number;
  depositPaymentToken?: string | null;
  depositPaymentExpiresAt?: string | null;
  depositPaymentStatus?: 'PENDING' | 'PAID' | 'EXPIRED' | 'CANCELLED';
  depositPaidAt?: string | null;
  rentArrears?: number;
  utilityArrears?: number;
  totalArrears?: number;
  unit?: {
    id: string;
    unitNumber: string;
    property: {
      id: string;
      name: string;
      address: string;
      allowPartialPayments?: boolean;
      enableLateFees?: boolean;
    };
  };
  lease?: {
    id: string;
    startDate: string;
    endDate: string;
    monthlyRent: number;
    documentStatus?: 'DRAFT' | 'PENDING_SIGNATURE' | 'SIGNED' | null;
    signedAt?: string | null;
  };
  emergencyContact?: EmergencyContact;
  sponsor?: Sponsor;
}

export interface Payment {
  id: string;
  amount: number;
  status: 'PAID' | 'UNPAID' | 'PARTIAL' | 'OVERDUE' | 'CANCELLED' | 'REFUNDED';
  type: 'RENT' | 'INITIAL' | 'DEPOSIT' | 'UTILITY' | 'MAINTENANCE' | 'LATE_FEE' | 'OTHER';
  dueDate: string;
  paidDate?: string;
  description?: string;
  tenantId: string;
  propertyId: string;
  unitId: string;
  rentSchedule?: {
    month?: number;
    year?: number;
    dueDate?: string;
  };
}

export interface MaintenanceRequest {
  id: string;
  title: string;
  description: string;
  status: 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED';
  priority: 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT';
  category: string;
  createdAt: string;
  updatedAt: string;
  tenantId: string;
  unitId: string;
  propertyId: string;
}

export interface Announcement {
  id: string;
  title: string;
  message: string;
  category: 'GENERAL' | 'MAINTENANCE' | 'PAYMENT' | 'EMERGENCY' | 'EVENT';
  priority: 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT';
  createdAt: string;
  scheduledDate?: string;
  isRead: boolean;
}

export interface Invoice {
  id: string;
  invoiceNumber: string;
  tenantId: string;
  propertyId: string;
  unitId: string;
  type: 'RENT' | 'DEPOSIT' | 'MAINTENANCE' | 'UTILITY' | 'LATE_FEE' | 'OTHER';
  status: 'DRAFT' | 'SENT' | 'PAID' | 'OVERDUE' | 'CANCELLED' | 'REFUNDED';
  amount: number;
  dueDate: string;
  issuedDate: string;
  paidAmount: number;
  paidAt?: string;
  paymentMethod?: string;
  description?: string;
  notes?: string;
  terms?: string;
  lineItems: InvoiceLineItem[];
  createdAt: string;
  updatedAt: string;
}

export interface InvoiceLineItem {
  id: string;
  invoiceId: string;
  description: string;
  quantity: number;
  unitPrice: number;
  amount: number;
  category?: string;
  taxRate?: number;
  taxAmount?: number;
  createdAt: string;
  updatedAt: string;
}

export interface Receipt {
  id: string;
  receiptNumber: string;
  tenantId: string;
  propertyId: string;
  unitId: string;
  type: 'RENT_PAYMENT' | 'MAINTENANCE_PAYMENT' | 'UTILITY_PAYMENT' | 'DEPOSIT_PAYMENT' | 'OTHER';
  amount: number;
  paymentDate: string;
  paymentMethod: string;
  invoiceId?: string;
  paymentId?: string;
  description?: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

class TenantService {
  // Ensure baseUrl always includes /api prefix
  private baseUrl = (() => {
    const url = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
    // If URL doesn't end with /api, add it
    return url.endsWith('/api') ? url : `${url.replace(/\/$/, '')}/api`;
  })();

  // Get auth token from local JWT store
  private getAuthToken(): string | null {
    return authTokenService.getToken();
  }

  // Authenticated fetch — attaches JWT and handles 401 session expiry
  private async authenticatedFetch(url: string, options: RequestInit = {}): Promise<Response> {
    const token = this.getAuthToken();
    const headers = {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...options.headers,
    };
    const res = await fetch(url, { ...options, headers });
    if (res.status === 401) {
      dispatchSessionExpired();
      throw new TenantApiError('SESSION_EXPIRED', MESSAGES.SESSION_EXPIRED);
    }
    return res;
  }


  private throwForResponse(response: Response, context: string): never {
    if (response.status === 401) {
      dispatchSessionExpired();
      throw new TenantApiError('SESSION_EXPIRED', MESSAGES.SESSION_EXPIRED);
    }
    if (response.status === 403) {
      // Try to read the JSON body to get a specific error code/message (e.g. PROPERTY_SUSPENDED)
      // We clone so we don't consume the body twice
      try {
        response.clone().json().then((body: any) => {
          if (body?.code === 'PROPERTY_SUSPENDED') {
            // Dispatch event for UI to show dedicated suspended screen
            window.dispatchEvent(new CustomEvent('property:suspended', {
              detail: { message: body.message || 'This property has been suspended by the administrator.' }
            }));
          }
        }).catch(() => { /* ignore */ });
      } catch { /* ignore */ }
      throw new TenantApiError('FORBIDDEN', MESSAGES.FORBIDDEN);
    }
    throw new TenantApiError('FETCH_FAILED', MESSAGES.FETCH_FAILED);
  }

  // Get tenant portal data
  async getTenantData(tenantId: string): Promise<TenantData> {
    const response = await this.authenticatedFetch(`${this.baseUrl}/tenants/portal/${tenantId}`);
    if (!response.ok) this.throwForResponse(response, 'tenant data');
    return response.json();
  }

  // Get tenant payments
  async getTenantPayments(tenantId: string): Promise<Payment[]> {
    const response = await this.authenticatedFetch(`${this.baseUrl}/tenant-payments/${tenantId}`);
    if (response.status === 404) return [];
    if (!response.ok) this.throwForResponse(response, 'payments');
    const data = await response.json();
    return data.payments || [];
  }

  // Get maintenance requests for tenant
  async getMaintenanceRequests(tenantId: string): Promise<MaintenanceRequest[]> {
    const response = await this.authenticatedFetch(`${this.baseUrl}/maintenance-requests?tenantId=${tenantId}`);
    if (response.status === 404) return [];
    if (!response.ok) this.throwForResponse(response, 'maintenance');
    const data = await response.json();
    return data.requests || [];
  }

  // Get announcements for tenant
  async getAnnouncements(tenantId: string): Promise<Announcement[]> {
    const response = await this.authenticatedFetch(`${this.baseUrl}/tenant-announcements/${tenantId}`);
    if (response.status === 404) return [];
    if (!response.ok) this.throwForResponse(response, 'announcements');
    const data = await response.json();
    return data.announcements || [];
  }

  // Create maintenance request
  async createMaintenanceRequest(tenantId: string, request: {
    title: string;
    description: string;
    priority: 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT';
    category: string;
  }): Promise<MaintenanceRequest> {
    const response = await this.authenticatedFetch(`${this.baseUrl}/maintenance-requests`, {
      method: 'POST',
      body: JSON.stringify({ ...request, tenantId, status: 'PENDING' })
    });
    if (!response.ok) this.throwForResponse(response, 'create maintenance');
    return response.json();
  }

  // Mark announcement as read
  async markAnnouncementAsRead(announcementId: string, tenantId: string): Promise<void> {
    const response = await this.authenticatedFetch(`${this.baseUrl}/tenant-announcements/${announcementId}/read`, {
      method: 'POST',
      body: JSON.stringify({ tenantId })
    });
    if (!response.ok) this.throwForResponse(response, 'mark read');
  }

  // Get tenant settings
  async getTenantSettings(tenantId: string): Promise<any> {
    const response = await this.authenticatedFetch(`${this.baseUrl}/tenant-settings/${tenantId}`);
    if (!response.ok) this.throwForResponse(response, 'settings');
    return response.json();
  }

  // Update tenant settings
  async updateTenantSettings(tenantId: string, settings: any): Promise<any> {
    const response = await this.authenticatedFetch(`${this.baseUrl}/tenant-settings/${tenantId}`, {
      method: 'PUT',
      body: JSON.stringify(settings)
    });
    if (!response.ok) this.throwForResponse(response, 'update settings');
    return response.json();
  }

  // Get tenant invoices
  async getTenantInvoices(tenantId: string): Promise<Invoice[]> {
    const apiUrl = this.baseUrl.endsWith('/api') ? this.baseUrl : `${this.baseUrl}/api`;
    const response = await this.authenticatedFetch(`${apiUrl}/tenant-invoices/${tenantId}`);
    if (response.status === 404) return [];
    if (!response.ok) this.throwForResponse(response, 'invoices');
    const data = await response.json();
    return data.data || [];
  }

  // Get specific invoice
  async getInvoice(tenantId: string, invoiceId: string): Promise<Invoice> {
    const response = await this.authenticatedFetch(`${this.baseUrl}/tenant-invoices/${tenantId}/${invoiceId}`);
    if (!response.ok) this.throwForResponse(response, 'invoice');
    const data = await response.json();
    return data.data;
  }

  // Download invoice PDF
  async downloadInvoicePDF(tenantId: string, invoiceId: string): Promise<Blob> {
    const response = await this.authenticatedFetch(`${this.baseUrl}/tenant-invoices/${tenantId}/${invoiceId}/pdf`);
    if (!response.ok) this.throwForResponse(response, 'invoice PDF');
    return response.blob();
  }

  // Get lease for tenant (portal) – always fetch fresh so we get currentDocumentUrl after landlord saves
  async getTenantLease(tenantId: string): Promise<{ lease: any; status: any } | null> {
    const url = `${this.baseUrl}/tenants/portal/${tenantId}/lease?t=${Date.now()}`;
    const response = await this.authenticatedFetch(url);
    if (response.status === 404) return null;
    if (!response.ok) this.throwForResponse(response, 'lease');
    return response.json();
  }

  // Sign lease (tenant)
  async signTenantLease(tenantId: string): Promise<{ signedAt: string }> {
    const response = await this.authenticatedFetch(`${this.baseUrl}/tenants/portal/${tenantId}/lease/sign`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
    if (!response.ok) this.throwForResponse(response, 'sign lease');
    return response.json();
  }

  // Full URL for viewing lease document (tenant-specific PDF, generated on the fly)
  getTenantLeaseDocumentViewUrl(tenantId: string): string {
    return `${this.baseUrl}/tenants/portal/${tenantId}/lease/document`;
  }

  // Download lease PDF (tenant) – returns blob for download/print (tenant-specific, generated on the fly)
  async getTenantLeaseDownload(tenantId: string): Promise<Blob> {
    const response = await this.authenticatedFetch(`${this.baseUrl}/tenants/portal/${tenantId}/lease/download`);
    if (!response.ok) this.throwForResponse(response, 'lease PDF');
    return response.blob();
  }

  // Get tenant receipts
  async getTenantReceipts(tenantId: string): Promise<Receipt[]> {
    const apiUrl = this.baseUrl.endsWith('/api') ? this.baseUrl : `${this.baseUrl}/api`;
    const response = await this.authenticatedFetch(`${apiUrl}/tenant-invoices/receipts/${tenantId}`);
    if (response.status === 404) return [];
    if (!response.ok) this.throwForResponse(response, 'receipts');
    const data = await response.json();
    return data.data || [];
  }

  // Get specific receipt
  async getReceipt(tenantId: string, receiptId: string): Promise<Receipt> {
    const response = await this.authenticatedFetch(`${this.baseUrl}/tenant-invoices/receipts/${tenantId}/${receiptId}`);
    if (!response.ok) this.throwForResponse(response, 'receipt');
    const data = await response.json();
    return data.data;
  }

  // Download receipt PDF
  async downloadReceiptPDF(tenantId: string, receiptId: string): Promise<Blob> {
    const response = await this.authenticatedFetch(`${this.baseUrl}/tenant-invoices/receipts/${tenantId}/${receiptId}/pdf`);
    if (!response.ok) this.throwForResponse(response, 'receipt PDF');
    return response.blob();
  }

  // Get outstanding balance for tenant
  async getOutstandingBalance(tenantId: string): Promise<{ outstandingBalance: number; allowPartialPayments: boolean }> {
    const response = await this.authenticatedFetch(`${this.baseUrl}/tenant-payments/${tenantId}/outstanding`);
    if (!response.ok) this.throwForResponse(response, 'outstanding');
    return response.json();
  }

  // Get outstanding utility payments (optional with rent, or separately)
  async getOutstandingUtilities(tenantId: string): Promise<{
    success: boolean;
    totalOutstanding: number;
    count: number;
    payments: Array<{ id: string; amount: number; status: string; dueDate?: string; notes?: string; type: string }>;
  }> {
    const response = await this.authenticatedFetch(`${this.baseUrl}/tenant-payments/${tenantId}/utilities-outstanding`);
    if (!response.ok) this.throwForResponse(response, 'outstanding utilities');
    return response.json();
  }

  // Make partial payment
  async makePartialPayment(tenantId: string, amount: number, frequency?: number, notes?: string): Promise<{
    success: boolean;
    payment: { id: string; amount: number; status: string; isPartial: boolean; partialPaymentNumber?: number; createdAt: string };
    checkoutUrl?: string;
  }> {
    const response = await this.authenticatedFetch(`${this.baseUrl}/tenant-payments/${tenantId}/partial`, {
      method: 'POST',
      body: JSON.stringify({ amount, frequency, notes })
    });
    if (!response.ok) {
      try {
        const err = await response.json();
        throw new TenantApiError('FETCH_FAILED', err.error || MESSAGES.FETCH_FAILED);
      } catch (e) {
        if (e instanceof TenantApiError) throw e;
        throw new TenantApiError('FETCH_FAILED', MESSAGES.FETCH_FAILED);
      }
    }
    return response.json();
  }

  // Notifications (message center) and push
  async getNotifications(tenantId: string, params?: { limit?: number; offset?: number; unreadOnly?: boolean }): Promise<{ items: TenantNotificationItem[]; total: number }> {
    const q = new URLSearchParams();
    if (params?.limit != null) q.set('limit', String(params.limit));
    if (params?.offset != null) q.set('offset', String(params.offset));
    if (params?.unreadOnly) q.set('unreadOnly', 'true');
    const response = await this.authenticatedFetch(`${this.baseUrl}/tenant-notifications/${tenantId}?${q.toString()}`);
    if (!response.ok) this.throwForResponse(response, 'notifications');
    return response.json();
  }

  async getNotificationUnreadCount(tenantId: string): Promise<number> {
    const response = await this.authenticatedFetch(`${this.baseUrl}/tenant-notifications/${tenantId}/unread-count`);
    if (!response.ok) this.throwForResponse(response, 'unread count');
    const data = await response.json();
    return data.count ?? 0;
  }

  async markNotificationRead(tenantId: string, notificationId: string): Promise<void> {
    const response = await this.authenticatedFetch(`${this.baseUrl}/tenant-notifications/${tenantId}/${notificationId}/read`, { method: 'PATCH' });
    if (!response.ok) this.throwForResponse(response, 'mark read');
  }

  async markAllNotificationsRead(tenantId: string): Promise<void> {
    const response = await this.authenticatedFetch(`${this.baseUrl}/tenant-notifications/${tenantId}/read-all`, { method: 'PATCH' });
    if (!response.ok) this.throwForResponse(response, 'mark all read');
  }

  async subscribePush(tenantId: string, subscription: { endpoint: string; keys: { p256dh: string; auth: string }; userAgent?: string; deviceType?: string }): Promise<void> {
    const response = await this.authenticatedFetch(`${this.baseUrl}/tenant-notifications/${tenantId}/push/subscribe`, {
      method: 'POST',
      body: JSON.stringify(subscription),
    });
    if (!response.ok) this.throwForResponse(response, 'push subscribe');
  }
}

export interface TenantNotificationItem {
  id: string;
  tenantId: string;
  type: string;
  title: string;
  body: string;
  read: boolean;
  readAt: string | null;
  data?: { action?: string; url?: string; entityId?: string };
  createdAt: string;
}

export const tenantService = new TenantService();
